import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { graphql, QueryRenderer, ReactRelayContext } from 'react-relay';
import { Link } from 'found';
import cx from 'classnames';
import MarkerPopupBottom from '../MarkerPopupBottom';
import CityBikeAvailability from '../../CityBikeAvailability';
import {
  BIKESTATION_ON,
  getCityBikeNetworkConfig,
  getCityBikeNetworkIcon,
  getCityBikeNetworkId,
  getCityBikeType,
} from '../../../util/citybikes';
import Loading from '../../Loading';
import Card from '../../Card';
import Icon from '../../Icon';

const CitybikeCardHeader = ({
  className,
  children,
  headerIcon,
  headingStyle,
  secondaryLink,
  name,
  url,
  description,
  code,
  externalLink,
  icon,
  unlinked,
}) => (
  <div
    className={cx('card-header', className)}
    style={{ overflowX: 'visible' }}
  >
    {children}
    <div className="card-header-content" style={{ paddingRight: '22px' }}>
      {icon ? (
        <div
          className="left"
          style={{ fontSize: 32, paddingRight: 10, height: 32 }}
        >
          <Icon img={icon} />
        </div>
      ) : null}
      {className === 'stop-page header' && (
        <div className="stop-page-header_icon-container">
          <Icon img="icon-icon_bus-stop" className="stop-page-header_icon" />
        </div>
      )}
      <div className="card-header-wrapper">
        {secondaryLink && (
          <div className="card-header-secondary-link">{secondaryLink}</div>
        )}
        {className === 'bike card' ? (
          <span className={headingStyle || 'h4'}>
            <a
              href="https://ratas.tartu.ee"
              target="_blank"
              rel="noopener noreferrer"
            >
              {name}
            </a>
            {externalLink || null}
            {headerIcon}
            {unlinked ? null : <span className="link-arrow"> ›</span>}
          </span>
        ) : (
          <span className={headingStyle || 'h4'}>
            {url ? <Link to={url}>{name}</Link> : `${name}`}
            {externalLink || null}
            {headerIcon}
            {unlinked ? null : <span className="link-arrow"> ›</span>}
          </span>
        )}
        <div className="card-sub-header">
          {code != null ? <p className="card-code">{code}</p> : null}
          {description != null && description !== 'null' ? (
            <p className="sub-header-h4">{description}</p>
          ) : null}
        </div>
      </div>
    </div>
  </div>
);

CitybikeCardHeader.propTypes = {
  className: PropTypes.string,
  headerIcon: PropTypes.node,
  headingStyle: PropTypes.string,
  secondaryLink: PropTypes.node,
  name: PropTypes.string.isRequired,
  url: PropTypes.string,
  description: PropTypes.string,
  code: PropTypes.string,
  externalLink: PropTypes.node,
  icon: PropTypes.string,
  children: PropTypes.node,
  unlinked: PropTypes.bool,
};

const CityBikeContent = (
  { station, locationPopup, onSelectLocation },
  { config },
) => {
  const networkConfig = getCityBikeNetworkConfig(
    getCityBikeNetworkId(station.networks),
    config,
  );

  const description = config.cityBike.showStationId ? station.stationId : '';

  return (
    <Card className="padding-small">
      <CitybikeCardHeader
        description={
          <>
            <FormattedMessage
              id="bike-rent-station-id"
              defaultMessage="Station ID"
            />
            {':\u00a0'}
            {description}
          </>
        }
        icon={getCityBikeNetworkIcon(networkConfig)}
        name={station.name}
        className="bike card"
        unlinked
      />
      <div className="city-bike-container">
        {station.state !== BIKESTATION_ON ? (
          <p className="sub-header-h4 availability-header">
            <FormattedMessage
              id="citybike_off"
              defaultMessage="Bike station closed"
            />
          </p>
        ) : (
          <CityBikeAvailability
            bikesAvailable={station.bikesAvailable}
            // pedelecBikesAvailable={station.pedelecBikesAvailable} // TODO: get
            pedelecBikesAvailable={0}
            freeDocks={station.spacesAvailable}
            totalSpaces={station.bikesAvailable + station.spacesAvailable}
            fewAvailableCount={config.cityBike.fewAvailableCount}
            type={getCityBikeType(station.networks, config)}
          />
        )}
      </div>
      {(locationPopup === 'all' || locationPopup === 'origindestination') && (
        <MarkerPopupBottom
          location={{
            address: station.name,
            lat: station.lat,
            lon: station.lon,
          }}
          locationPopup={locationPopup}
          onSelectLocation={onSelectLocation}
        />
      )}
    </Card>
  );
};

CityBikeContent.propTypes = {
  station: PropTypes.object.isRequired,
  locationPopup: PropTypes.string,
  onSelectLocation: PropTypes.func,
};

CityBikeContent.contextTypes = {
  config: PropTypes.object.isRequired,
};

const CityBikePopup = props => {
  const { stationId } = props;
  const { environment } = useContext(ReactRelayContext);

  return (
    <QueryRenderer
      query={graphql`
        query CityBikePopupQuery($stationId: String!) {
          station: bikeRentalStation(id: $stationId) {
            stationId
            name
            lat
            lon
            bikesAvailable
            spacesAvailable
            state
            networks
          }
        }
      `}
      variables={{
        stationId,
      }}
      environment={environment}
      render={({ props: innerProps }) =>
        innerProps ? (
          <CityBikeContent {...innerProps} {...props} />
        ) : (
          <Loading />
        )
      }
    />
  );
};

CityBikePopup.propTypes = {
  stationId: PropTypes.string.isRequired,
};

export default CityBikePopup;
